import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { Check } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

export interface TrackMilestone {
  title: string;
  targetProgress: number;
  completed: boolean;
}

export interface ProgressTrackProps {
  /** The stored progress, 0-100. */
  value: number;
  milestones: TrackMilestone[];
  /** The people on the task can move the handle and tick milestones; everyone else just reads it. */
  interactive: boolean;
  /** Called once when the handle is let go (or a key is released), with the new value. */
  onCommit: (value: number) => void;
  /** Called with the milestone's index in `milestones` when its stop is clicked. Without it the stops only show. */
  onToggle?: (index: number) => void;
  disabled?: boolean;
  /** A bare preview of where the stops sit (the new-task form): no handle, no labels. */
  preview?: boolean;
  /** Names and percentages under the stops (default). The submit page keeps the track bare. */
  labels?: boolean;
}

const clamp = (n: number) => Math.min(100, Math.max(0, Math.round(n)));

/** Keeps a label from running off either end of the card: stops near the edges align to them. */
const labelPlacement = (pct: number) =>
  pct >= 90
    ? 'right-0 items-end text-right'
    : pct <= 10
      ? 'left-0 items-start text-left'
      : '-translate-x-1/2 items-center text-center';

/**
 * Figma A05.4 "Progress": a dark track filled to the stored value, a handle on it, and a named stop for each
 * milestone (a check once it is ticked). The handle is the specialist's manual slider; the stops are a separate
 * checklist, so moving one never moves the other.
 */
export function ProgressTrack({
  value,
  milestones,
  interactive,
  onCommit,
  onToggle,
  disabled,
  preview = false,
  labels = true,
}: ProgressTrackProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  // While dragging the handle follows the pointer; once let go the stored value takes over again.
  const [drag, setDrag] = useState<number | null>(null);
  const shown = drag ?? value;

  const fromPointer = (e: PointerEvent) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return shown;
    return clamp(((e.clientX - rect.left) / rect.width) * 100);
  };

  const live = interactive && !disabled;
  const finish = (next: number) => {
    setDrag(null);
    if (next !== value) onCommit(next);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 1;
    const next =
      e.key === 'ArrowRight' || e.key === 'ArrowUp'
        ? shown + step
        : e.key === 'ArrowLeft' || e.key === 'ArrowDown'
          ? shown - step
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? 100
              : null;
    if (next === null) return;
    e.preventDefault();
    setDrag(clamp(next));
  };

  return (
    <div className="flex flex-col gap-(--mbm-space-3)">
      <div
        ref={trackRef}
        role={live ? 'slider' : 'progressbar'}
        aria-label="Task progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={shown}
        aria-valuetext={`${shown}%`}
        tabIndex={live ? 0 : undefined}
        onPointerDown={
          live
            ? (e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                setDrag(fromPointer(e));
              }
            : undefined
        }
        onPointerMove={
          live ? (e) => e.currentTarget.hasPointerCapture(e.pointerId) && setDrag(fromPointer(e)) : undefined
        }
        onPointerUp={live ? (e) => finish(fromPointer(e)) : undefined}
        onPointerCancel={live ? () => setDrag(null) : undefined}
        onKeyDown={live ? onKeyDown : undefined}
        onKeyUp={live ? () => drag !== null && finish(drag) : undefined}
        className={cx(
          'relative flex h-(--mbm-size-slider-thumb) touch-none items-center rounded-full focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
          live && 'cursor-pointer',
        )}
      >
        <div className="h-(--mbm-space-1-5) w-full rounded-full bg-surface-tint" />
        <div
          className="absolute left-0 h-(--mbm-space-1-5) rounded-full bg-action-primary"
          style={{ width: `${shown}%` }}
        />
        {milestones.map((m, index) => (
          <button
            key={`${m.title}-${m.targetProgress}`}
            type="button"
            disabled={!live || !onToggle}
            aria-pressed={m.completed}
            aria-label={`${m.title}, ${m.targetProgress}%${m.completed ? ', done' : ''}`}
            // A click on a stop ticks it; it must not start a drag of the handle underneath.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onToggle?.(index)}
            className={cx(
              'absolute flex size-(--mbm-size-milestone-marker) -translate-x-1/2 items-center justify-center rounded-full border-2',
              'focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
              m.completed
                ? 'border-action-primary bg-action-primary text-text-on-dark'
                : 'border-border-strong bg-surface-card text-transparent',
              live && onToggle ? 'cursor-pointer' : 'cursor-default',
            )}
            style={{ left: `${m.targetProgress}%` }}
          >
            <Icon icon={Check} size="xs" />
          </button>
        ))}
        {preview ? null : (
          <span
            aria-hidden
            className="pointer-events-none absolute size-(--mbm-size-slider-thumb) -translate-x-1/2 rounded-full border-4 border-action-primary bg-surface-card"
            style={{ left: `${shown}%` }}
          />
        )}
      </div>
      {preview || !labels ? null : (
        <div className="relative h-(--mbm-space-7)">
          {milestones.map((m) => (
            <span
              key={`${m.title}-${m.targetProgress}-label`}
              className={cx('absolute top-0 flex max-w-[28%] flex-col', labelPlacement(m.targetProgress))}
              style={m.targetProgress > 10 && m.targetProgress < 90 ? { left: `${m.targetProgress}%` } : undefined}
            >
              <span className="text-label-small text-text-primary">{m.title}</span>
              <span className="text-caption text-text-secondary">{m.targetProgress}%</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
